import { useState, useEffect, useCallback, useRef } from "react";
import { fetchAvSyncStatus } from "../../../services/syncStatusService";
import { POLL_STATUS, POLL_INTERVAL_MS } from "../../../constants";

export { POLL_STATUS };

export function useSyncStatus(sessionId, userEmail = "") {
  const [pollStatus, setPollStatus] = useState(POLL_STATUS.POLLING);
  const [pollError, setPollError] = useState("");
  const [languageStatuses, setLanguageStatuses] = useState([]);
  const [lastChecked, setLastChecked] = useState(null); // Date of last successful poll
  const [isChecking, setIsChecking] = useState(false); // true while fetch in-flight

  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Extract jobId — sessionId is the { jobId, videoKey, srtKey } object
  const jobId = sessionId?.jobId ?? sessionId ?? null;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async () => {
    if (!jobId || !isMountedRef.current) return;

    setIsChecking(true);

    try {
      const result = await fetchAvSyncStatus(jobId, userEmail);

      if (!isMountedRef.current) return;

      setLanguageStatuses(result.languageStatuses || []);
      setLastChecked(new Date());
      setIsChecking(false);

      if (result.allTranslated) {
        stopPolling();
        setPollStatus(POLL_STATUS.COMPLETE);
        // eslint-disable-next-line no-console
        console.log("[useSyncStatus] ✅ All languages VIDEO_TRANSLATED — sync complete.");
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setIsChecking(false);
      stopPolling();
      setPollError(err.message || "Failed to check sync status. Please try again.");
      setPollStatus(POLL_STATUS.ERROR);
    }
  }, [jobId, userEmail, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    setPollStatus(POLL_STATUS.POLLING);
    setPollError("");
    setLanguageStatuses([]);
    setLastChecked(null);
    pollOnce(); // immediate first tick
    intervalRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
  }, [stopPolling, pollOnce]);

  const retryPolling = useCallback(() => {
    startPolling();
  }, [startPolling]);

  useEffect(() => {
    isMountedRef.current = true;

    if (jobId) {
      // eslint-disable-next-line no-console
      console.log(
        `[useSyncStatus] Starting real polling — jobId: ${jobId}, ` +
          `interval: ${POLL_INTERVAL_MS / 1000}s, user: ${userEmail}`
      );
      startPolling();
    }

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [jobId, startPolling, stopPolling, userEmail]);

  return {
    pollStatus,
    pollError,
    languageStatuses,
    lastChecked,
    isChecking,
    retryPolling,
  };
}
